<script setup lang="ts">

import type {PortfolioFilters} from "~/types/Portfolio";
import type {SpecificationType} from "~/types/Specification";
import { Button } from '~/components/ui/button'
import SelectLocation from '~/components/custom-ui/SelectLocation.vue'
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group'
import { Slider } from '~/components/ui/slider'
import BtnFilterRangeInline from '~/components/custom-ui/filterFields/BtnFilterRangeInline.vue'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '~/components/ui/select'
import BtnFilterCheckbox from '~/components/custom-ui/filterFields/BtnFilterCheckbox.vue'
import BtnFilterSelect from '~/components/custom-ui/filterFields/BtnFilterSelect.vue'
import BtnFilterLabels from '~/components/custom-ui/filterFields/BtnFilterLabels.vue'
import BtnFilterLabelSingle from '~/components/custom-ui/filterFields/BtnFilterLabelSingle.vue'

const props = defineProps({
    filtersList: {
        type: Object as PropType<PortfolioFilters>,
        default: () => null
    },
    mastersCount: {
        type: Number,
        default: 0
    },
    parentSpecification: {
        type: Object as PropType<SpecificationType>,
        default: null
    },
    specificationChildList: {
        type: Array as PropType<SpecificationType[]>,
        default: () => []
    },
    filterForm: {
        type: Object,
        default: () => null
    }
})

const emit = defineEmits(['submit', 'back', 'closeFiltersModal'])

const route = useRoute();
const { t } = useI18n();
const { parentSpecifications, specifications } = useGlobalStore();

const FiltersModeEnum = {
    FILTERS: 'filters',
    PARENT_CATS: 'parent_category',
    CHILD_CATS: 'child_categories',
} as const;

const CurrencyEnum = {
    AED: 'AED',
    USD: 'USD',
    EUR: 'EUR',
} as const;

const activeMode = ref<typeof FiltersModeEnum[keyof typeof FiltersModeEnum]>(FiltersModeEnum.FILTERS);

const localFilters = ref({
    specificationId: [route.params.id as string],
    city: [],
    educationType: null,
    employmentType: null,
    isRemote: null,
    minLat: null,
    maxLat: null,
    minLng: null,
    maxLng: null,
    priceFrom: null,
    priceTo: null,
    pageSize: 100,
    page: 1,
    reviewsCount: null,
    experienceFrom: null,
})

const selectedLocalFilterSpecification = computed(() => {
    return specifications.find(item => item.documentId === localFilters.value.specificationId[0]) || null
})
const selectedParentCategoryId = ref<string | null | undefined>(null);

const priceRange = computed({
    get: () => [localFilters.value.priceFrom, localFilters.value.priceTo],
    set: ([from, to]) => {
        localFilters.value.priceFrom = from
        localFilters.value.priceTo = to
    }
})

function initialPriceRange(){
    if(props.filtersList?.price){
        priceRange.value = [props.filtersList.price.from, props.filtersList.price.to]
    }
}

initialPriceRange()

onMounted(() => {
    if(props.filterForm) localFilters.value = props.filterForm
})
</script>

<template>
<div class="">
    <div v-if="activeMode === FiltersModeEnum.FILTERS" class="grid gap-4">
        <div class="page_title">
            <div class="flex items-center gap-6">
                <Button class="hidden lg:flex p-3 h-auto text-black" variant="lighter" @click.prevent="emit('back')">
                    <Icon name="ri:arrow-left-line" size="24px"/>
                </Button>
                <Button class="flex lg:hidden p-3 h-auto text-black" variant="lighter" @click.prevent="emit('closeFiltersModal')">
                    <Icon name="ri:arrow-left-line" size="24px"/>
                </Button>
                <p class="font-semibold text-xl">Filters</p>
            </div>
        </div>
        <Button class="p-3 h-auto w-full rounded-lg text-dark justify-between mt-4" variant="outline" @click.prevent="activeMode = FiltersModeEnum.PARENT_CATS">
            <span class="font-medium text-base">{{ selectedLocalFilterSpecification?.name }}</span>
            <Icon name="ri:arrow-right-line" size="24px"/>
        </Button>
        <div class="grid grid-cols-12 gap-6 items-end">
            <div v-if="filtersList?.cities?.length" class="col-span-12">
                <p class="text-sm mb-2">{{ $t('Service area') }}</p>
                <BtnFilterCheckbox
                    v-model="localFilters.city"
                    :label="$t('Location')"
                    :options="filtersList?.cities"
                    icon="ri:user-location-line"
                    btn_class="max-h-[48px] h-[48px] px-2 w-full"
                    class="hidden lg:block" />
                <BtnFilterLabels
                    v-model="localFilters.city"
                    :label="null"
                    :options="filtersList?.cities"
                    class="lg:hidden" />
                <!-- <SelectLocation
                        v-model="localFilters.city"
                        :default="localFilters.city[0]" class="w-full"
                        :options="filtersList.cities"
                /> -->
            </div>
            <div class="col-span-12">
                <RadioGroup v-model="localFilters.isRemote" class="flex gap-2">
                    <label class="flex items-center justify-between space-x-2 border rounded-xl border-gray-200 px-4 py-3 w-1/2">
                        <span class="text-sm break-words">{{ $t('Remote') }}</span>
                        <RadioGroupItem class="text-primary-500" :value="true"/>
                    </label>
                    <label class="flex items-center justify-between space-x-2 border rounded-xl border-gray-200 px-4 py-3 w-1/2">
                        <span class="text-sm break-words">{{ $t('On Site') }}</span>
                        <RadioGroupItem class="text-primary-500" :value="false"/>
                    </label>
                </RadioGroup>
            </div>
        </div>
        <div v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to">
            <p class="text-sm mb-2">{{ $t('Price Range') }}</p>
            <BtnFilterRangeInline
                v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to"
                v-model:minValue="localFilters.priceFrom"
                v-model:maxValue="localFilters.priceTo"
                :min="filtersList.price.from"
                :max="filtersList.price.to"
                :label="null"
            />
            <!-- <Slider
                    v-model="priceRange"
                    :min="filtersList?.price?.from || 0"
                    :max="filtersList?.price?.to || 1000"
                    :step="100"
                    class="mt-4"
            />
            <BtnFilterRangeInline
                    v-model:min-value="localFilters.priceFrom"
                    v-model:max-value="localFilters.priceTo"
                    :min="filtersList?.price?.from || 0"
                    :max="filtersList?.price?.to || 1000"
                    class="mt-6"/> -->
        </div>

        <div class="grid lg:grid-cols-2 gap-4">
            <div v-if="filtersList?.experiences" class="col-span-2">
                <p class="text-sm mb-2">{{ $t('Work experience') }}</p>
                <BtnFilterSelect
                    v-model="localFilters.experienceFrom"
                    :label="$t('Experience')"
                    :options="filtersList.experiences"
                    icon="ri:briefcase-2-fill"
                    btn_class="max-h-[48px] h-[48px] px-2 w-full"
                    class="hidden lg:block" />
                <BtnFilterLabelSingle
                    v-model="localFilters.experienceFrom"
                    :label="null"
                    :options="filtersList.experiences"
                    option-label="label"
                    option-value="value"
                    icon="ri:briefcase-2-fill"
                    btn_class="max-h-[36px] px-2 w-full"
                    class="lg:hidden" 
                />
            </div>
        </div>
        <div v-if="filtersList?.employments" class="grid grid-cols-12 gap-4 items-end">
            <div class="col-span-12 lg:col-span-6">
                <p class="text-sm mb-2">{{ $t('Employment') }}</p>
                <BtnFilterCheckbox
                    v-model="localFilters.employmentType"
                    :label="$t('Employment')"
                    :options="filtersList.employments"
                    icon="ri:briefcase-2-fill"
                    btn_class="max-h-[48px] h-[48px]
                    px-2 w-full"
                    class="hidden lg:block" />
                <BtnFilterLabels
                    v-model="localFilters.employmentType"
                    :label="null"
                    :options="filtersList.employments"
                    class="lg:hidden"
                />
            </div>
            <div v-if="filtersList?.educations" class="col-span-12 lg:col-span-6">
                <p class="text-sm mb-2">{{ $t('Education') }}</p>
                <BtnFilterCheckbox
                    v-model="localFilters.educationType"
                    :label="$t('Education')"
                    :options="filtersList.educations"
                    icon="ri:book-line"
                    btn_class="max-h-[48px] h-[48px] px-2 w-full"
                    class="hidden lg:block" />
                <BtnFilterLabels
                    v-model="localFilters.educationType"
                    :label="null"
                    :options="filtersList.educations"
                    class="lg:hidden"
                />
            </div>
        </div>
        <div class="mt-0">
            <Button class="text-dark w-full p-6" variant="lighter" @click.prevent="emit('submit', localFilters)">
                {{ $t('Show') }}
            </Button>
            <div v-if="mastersCount" class="masters_count text-center text-gray-400 text-sm mt-2">
                {{ mastersCount }} {{ $t('specialists nearby') }}
            </div>
        </div>
    </div>

    <div v-if="activeMode === FiltersModeEnum.PARENT_CATS" class="aside order-2 lg:order-1 lg:col-span-4">
        <div class="page_title mb-8">
            <div class="flex items-center gap-6">
                <Button class="p-3 h-auto text-black" variant="lighter" @click.prevent="activeMode = FiltersModeEnum.FILTERS">
                    <Icon name="ri:arrow-left-line" size="24px"/>
                </Button>
                <p class="font-semibold text-xl">{{ $t('Main category') }}</p>
            </div>
        </div>

        <ul class="nav">
            <li v-for="item in parentSpecifications" class="border-b border-gray-200">
                <div
                        :class="{'font-bold': item.documentId === selectedParentCategoryId }"
                        class="list-container w-full py-2 transition-all hover:pl-4 rtl:hover:pr-4 flex items-center cursor-pointer"
                        @click.prevent="selectedParentCategoryId = item.documentId; activeMode = FiltersModeEnum.CHILD_CATS">
                    <span>{{ item.name }}</span>
                    <Icon class="ml-auto text-gray-500" name="ri:arrow-right-s-line" size="20px"/>
                </div>
            </li>
        </ul>
    </div>

    <div v-if="activeMode === FiltersModeEnum.CHILD_CATS" class="aside order-2 lg:order-1 lg:col-span-4">
        <div class="page_title mb-8">
            <div class="flex items-center gap-6">
                <Button class="p-3 h-auto text-black" variant="lighter">
                    <Icon name="ri:arrow-left-line" size="24px"/>
                </Button>
                <p class="font-semibold text-xl">{{ parentSpecification.name }}</p>
            </div>
        </div>

        <ul class="nav">
            <li v-for="item in specificationChildList" class="border-b border-gray-200">
                <div
                        :class="{'font-bold': item.documentId == filterForm.specificationId[0] }"
                        class="list-container w-full py-2 transition-all hover:pl-4 rtl:hover:pr-4 flex items-center cursor-pointer"
                        @click.prevent="localFilters.specificationId = [item.documentId]; activeMode = FiltersModeEnum.FILTERS">
                    <span>{{ item.name }}</span>
                    <Icon class="ml-auto text-gray-500" name="ri:arrow-right-s-line" size="20px"/>
                </div>
            </li>
        </ul>
    </div>
</div>
</template>